// src/positions/HiringRoomImportModal.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Pagination,
    Tooltip,
    Chip,
} from '@mui/material';
import {
    FileDownload as ImportIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const HiringRoomImportModal = ({ open, onClose, onImportComplete }) => {
    const { authToken, organization, project, showAlert } = useStore.getState();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [vacancies, setVacancies] = useState([]);
    const [selectedVacancies, setSelectedVacancies] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const pageSize = 20;

    // Cargar vacantes al abrir el modal o cambiar de página
    useEffect(() => {
        if (open) {
            loadVacancies(currentPage);
        }
    }, [open, currentPage]);

    const loadVacancies = async (page) => {
        try {
            setLoading(true);

            const response = await api.get(
                '/positions/hiringroom-vacancies',
                {
                    params: {
                        organizationId: organization,
                        projectId: project,
                        page: page,
                        pageSize: pageSize
                    },
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            if (response.data.response.status_response === 'OK') {
                const data = response.data.response.data;
                setVacancies(data.vacantes);
                setTotalPages(data.totalPaginas);
                setTotal(data.total);
            } else {
                showAlert(t('hiringRoomVacanciesImport.loadError') + ': ' + response.data.response.message, 'error');
            }
        } catch (err) {
            console.error('Error cargando vacantes:', err);
            showAlert(t('hiringRoomVacanciesImport.loadErrorDetail'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectVacancy = (vacancy, event) => {
        // Detener propagación si viene del checkbox
        if (event) {
            event.stopPropagation();
        }

        if (vacancy.alreadyImported) return;

        setSelectedVacancies(prev => {
            const isSelected = prev.some(v => v.id === vacancy.id);
            if (isSelected) {
                return prev.filter(v => v.id !== vacancy.id);
            } else {
                return [...prev, vacancy];
            }
        });
    };

    const handleSelectAll = () => {
        const availableVacancies = vacancies.filter(v => !v.alreadyImported);

        const allCurrentSelected = availableVacancies.every(v =>
            selectedVacancies.some(sv => sv.id === v.id)
        );

        if (allCurrentSelected) {
            setSelectedVacancies(prev =>
                prev.filter(sv => !availableVacancies.some(v => v.id === sv.id))
            );
        } else {
            const newSelections = availableVacancies.filter(
                v => !selectedVacancies.some(sv => sv.id === v.id)
            );
            setSelectedVacancies(prev => [...prev, ...newSelections]);
        }
    };

    const handleImport = async () => {
        if (selectedVacancies.length === 0) {
            showAlert(t('hiringRoomVacanciesImport.selectAtLeastOne'), 'warning');
            return;
        }

        try {
            setImporting(true);

            const response = await api.post(
                '/positions/import-hiringroom-vacancies',
                { vacancies: selectedVacancies },
                {
                    params: {
                        organizationId: organization,
                        projectId: project
                    },
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            if (response.data.response.status_response === 'OK') {
                const data = response.data.response.data;
                showAlert(
                    t('hiringRoomVacanciesImport.importSuccess', { count: data.totalImported }),
                    'success'
                );

                if (data.totalErrors > 0) {
                    console.warn('Errores en importación:', data.errors);
                    showAlert(
                        t('hiringRoomVacanciesImport.importErrors', { count: data.totalErrors }),
                        'warning'
                    );
                }

                setSelectedVacancies([]);
                loadVacancies(currentPage);

                if (onImportComplete) {
                    onImportComplete();
                }
            } else {
                showAlert(t('hiringRoomVacanciesImport.importError') + ': ' + response.data.response.message, 'error');
            }
        } catch (err) {
            console.error('Error importando vacantes:', err);
            showAlert(t('hiringRoomVacanciesImport.importErrorDetail'), 'error');
        } finally {
            setImporting(false);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value - 1);
        setSelectedVacancies([]);
    };

    const handleClose = () => {
        setSelectedVacancies([]);
        setCurrentPage(0);
        onClose();
    };

    const isVacancySelected = (vacancyId) => {
        return selectedVacancies.some(v => v.id === vacancyId);
    };

    const availableVacancies = vacancies.filter(v => !v.alreadyImported);
    const allCurrentSelected = availableVacancies.length > 0 &&
        availableVacancies.every(v => isVacancySelected(v.id));

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{t('hiringRoomVacanciesImport.title')}</Typography>
                    <Chip
                        label={`${total} ${t('hiringRoomVacanciesImport.vacanciesAvailable')}`}
                        color="primary"
                        size="small"
                    />
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {vacancies.length === 0 ? (
                            <Alert severity="info">
                                {t('hiringRoomVacanciesImport.noVacanciesAvailable')}
                            </Alert>
                        ) : (
                            <>
                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('hiringRoomVacanciesImport.pageOfPages', { current: currentPage + 1, total: totalPages })}
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={handleSelectAll}
                                        disabled={availableVacancies.length === 0}
                                    >
                                        {allCurrentSelected ? t('hiringRoomVacanciesImport.deselectAll') : t('hiringRoomVacanciesImport.selectAll')}
                                    </Button>
                                </Box>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" width={50}></TableCell>
                                                <TableCell><strong>{t('hiringRoomVacanciesImport.name')}</strong></TableCell>
                                                <TableCell><strong>{t('hiringRoomVacanciesImport.area')}</strong></TableCell>
                                                <TableCell><strong>{t('hiringRoomVacanciesImport.hierarchy')}</strong></TableCell>
                                                <TableCell><strong>{t('hiringRoomVacanciesImport.creationDate')}</strong></TableCell>
                                                <TableCell width={100}><strong>{t('hiringRoomVacanciesImport.status')}</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {vacancies.map((vacancy) => (
                                                <TableRow
                                                    key={vacancy.id}
                                                    hover={!vacancy.alreadyImported}
                                                    sx={{
                                                        cursor: vacancy.alreadyImported ? 'not-allowed' : 'pointer',
                                                        backgroundColor: vacancy.alreadyImported ? '#f5f5f5' : 'inherit',
                                                        opacity: vacancy.alreadyImported ? 0.6 : 1
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        {vacancy.alreadyImported ? (
                                                            <Tooltip title={t('hiringRoomVacanciesImport.alreadyImported')} arrow>
                                                                <BlockIcon color="disabled" fontSize="small" />
                                                            </Tooltip>
                                                        ) : (
                                                            <Checkbox
                                                                checked={isVacancySelected(vacancy.id)}
                                                                onChange={(e) => handleSelectVacancy(vacancy, e)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell onClick={() => !vacancy.alreadyImported && handleSelectVacancy(vacancy)}>
                                                        {vacancy.nombre}
                                                    </TableCell>
                                                    <TableCell onClick={() => !vacancy.alreadyImported && handleSelectVacancy(vacancy)}>
                                                        {vacancy.area?.nombre || t('hiringRoomVacanciesImport.notAvailable')}
                                                    </TableCell>
                                                    <TableCell onClick={() => !vacancy.alreadyImported && handleSelectVacancy(vacancy)}>
                                                        {vacancy.jerarquia || t('hiringRoomVacanciesImport.notAvailable')}
                                                    </TableCell>
                                                    <TableCell onClick={() => !vacancy.alreadyImported && handleSelectVacancy(vacancy)}>
                                                        {vacancy.fechaCreacion || t('hiringRoomVacanciesImport.notAvailable')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {vacancy.alreadyImported ? (
                                                            <Chip
                                                                label={t('hiringRoomVacanciesImport.imported')}
                                                                size="small"
                                                                color="success"
                                                                icon={<CheckCircleIcon />}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={vacancy.estadoActual}
                                                                size="small"
                                                                color="default"
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage + 1}
                                            onChange={handlePageChange}
                                            color="primary"
                                        />
                                    </Box>
                                )}

                                {selectedVacancies.length > 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {t('hiringRoomVacanciesImport.vacanciesSelected', { count: selectedVacancies.length })}
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={importing}>
                    {t('hiringRoomVacanciesImport.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={loading || importing || selectedVacancies.length === 0}
                    startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
                >
                    {importing
                        ? t('hiringRoomVacanciesImport.importing')
                        : t('hiringRoomVacanciesImport.importSelected', { count: selectedVacancies.length })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HiringRoomImportModal;